/**
 * "How this figure is calculated" — the demo's `calc` notes, in one place.
 *
 * Sixteen of the demo's `FIG(label, srcKey, filter, opts)` call sites pass an
 * `opts.calc` string, which `audit()` prints in a box above the source rows
 * (line 1909). The notes are the demo's words; the port adds `steps` — the
 * same rule with the figures the scene showed — so the reader can check the
 * arithmetic against the rows underneath.
 *
 * The rule strings are verbatim, keyed by what they explain, with the demo
 * line each came from. Scenes build a {@link SourceCalc} from a note plus their
 * own numbers; the builders below cover the notes more than one scene uses.
 *
 * Pure. Shared layer, fork-owned (zone C).
 *
 * @see src/versions/v1/chrome-oem/SourceDrawerBody.tsx — `SourceCalc`, the box
 */

import type { SourceCalc } from '../chrome-oem/SourceDrawerBody';
import type { WorkOrder } from '../schema/work-order';
import { fmt$ } from './oem-engine';

/** The demo's calc notes, verbatim. */
export const CALC_NOTE = {
  /** Line 2125 — overview "Fleet availability · week to 06 Sep". */
  fleetAvailability:
    'Availability % = engine hours ÷ (shifts × 9.5 scheduled operating hours) per machine, averaged across the fleet. Source: Pitram shift-end records and live telematics rollup.',
  /** Line 2126 — overview "Emissions · week to 06 Sep". */
  fleetEmissions:
    't CO₂-e = Σ (diesel L × 2.68) + Σ (kWh × site grid factor) over the week, ÷ 1000. Factors in energy_factors.csv.',
  /** Line 2157 — division KPI cards. */
  divisionAvailability:
    'Availability % = engine hours ÷ (shifts × 9.5 scheduled operating h), week to 06 Sep, mean over machines.',
  /** Line 2201 — a work order's all-in figure on the machine history. */
  workOrderAllIn:
    'All-in = labour + parts + freight + external + lost production (downtime h × class rate at this mine).',
  /** Line 2220 — machine Cost tab, "All-in cost · 18 months". */
  machineAllIn:
    'Sum of every ledger line for this machine: labour, parts (incl. freight uplift), freight, external service and lost production.',
  /** Lines 2597, 2603 — weekly report and risk email, availability. */
  reportAvailability:
    'Availability = Σ engine hours ÷ (machine-days × 2 shifts × 9.5 scheduled operating h)',
  /** Line 2603 — weekly report, cost this week. */
  reportCost:
    'Σ (labour + parts + freight + external) + Σ lost production (downtime h × class rate at the mine)',
  /** Line 2620 — deck slide 2, availability. */
  deckAvailability: 'Σ engine hours ÷ (machine-days × 2 shifts × 9.5 h)',
  /** Line 2620 — deck slide 2, CO₂-e this week. */
  deckEmissions: 'Σ (diesel L × 2.68 + kWh × grid factor) ÷ 1000',
  /** Line 3138 — alert card, "If it fails first" rate. */
  rateCard:
    'Lost production A$/h by machine class and site, from the rate card. Unplanned = downtime hours × rate + the parts the failure takes with it + the extra labour.',
} as const;

/**
 * Demo lines 2201, 2297, 2305, 2313. A work order's all-in cost, worked from
 * its own cost columns. `detail` annotates the parts figure the way the
 * incident tiles do, e.g. `'3 pipes + bit'`.
 */
export function workOrderAllInCalc(
  wo: Pick<
    WorkOrder,
    | 'labour_cost_aud'
    | 'parts_cost_aud'
    | 'freight_cost_aud'
    | 'external_cost_aud'
    | 'downtime_cost_aud'
    | 'total_direct_cost_aud'
  >,
  detail?: string,
): SourceCalc {
  const parts = [
    `Labour ${fmt$(wo.labour_cost_aud)}`,
    `parts ${fmt$(wo.parts_cost_aud)}${detail ? ` (${detail})` : ''}`,
    `freight ${fmt$(wo.freight_cost_aud)}`,
    ...(wo.external_cost_aud ? [`external ${fmt$(wo.external_cost_aud)}`] : []),
    `lost production ${fmt$(wo.downtime_cost_aud || 0)}`,
  ];
  return {
    formula: CALC_NOTE.workOrderAllIn,
    steps: [
      parts.join(' + '),
      `= ${fmt$(wo.total_direct_cost_aud + (wo.downtime_cost_aud || 0))}`,
    ],
  };
}

/**
 * Demo line 2225. Machine Energy tab, "Emissions intensity" — the demo's note
 * is itself the worked step, so the rule is stated once and the step carries
 * the figures.
 */
export function emissionsIntensityCalc(
  energy: { diesel_l: number; energy_kwh: number; hours: number; co2_kg_per_hour: number },
  gridKgPerKwh: number,
): SourceCalc {
  return {
    formula:
      'kg CO₂-e per operating hour = (diesel L × 2.68 + kWh × site grid factor) ÷ operating hours.',
    steps: [
      `(${energy.diesel_l.toLocaleString('en-AU')} L × 2.68 + ${energy.energy_kwh.toLocaleString('en-AU')} kWh × ${gridKgPerKwh}) ÷ ${energy.hours} h = ${energy.co2_kg_per_hour}`,
    ],
  };
}
